// Static definitions for the map v2 layers (see docs/map-v2/plan.md).
// Indicator labels come from the maquette (carto-b.html); they are French display strings.

export const NO_INDICATOR = '–';

export const LAYER_KEYS = ['sessions', 'reference', 'network', 'projects'] as const;
export type LayerKey = typeof LAYER_KEYS[number];

export const LAYER_LABELS: Record<LayerKey, string> = {
    sessions: 'Sessions',
    reference: 'Référentiel',
    network: 'État du réseau',
    projects: 'Projets',
};

// Layers that are wired to real data. The others are shown as "à venir" and cannot be enabled yet.
// Order of delivery: reference, sessions, network, projects.
export const LAYER_AVAILABILITY: Record<LayerKey, boolean> = {
    sessions: false,
    reference: true,
    network: false,
    projects: false,
};

export const LAYER_INDICATORS: Record<LayerKey, readonly string[]> = {
    sessions: [NO_INDICATOR, 'Note globale', 'Note par type de dégradation', 'Note largeur', "Date d'acquisition"],
    reference: [
        NO_INDICATOR,
        'Catégorie',
        'Type de chaussée',
        'Urbain',
        'Trafic PL',
        'Année',
        'Largeur',
        'Gestionnaire',
        'Axe',
    ],
    network: [
        NO_INDICATOR,
        'État global',
        'Fraîcheur de scan',
        'Note surface',
        'Note structure',
        'Note fissuration',
        'Note arrachement',
        'Note réparation',
        'Note remontées',
        'Note déformation',
        'Note largeur',
    ],
    projects: [
        NO_INDICATOR,
        'Priorité',
        "Type d'entretien",
        "Année d'entretien",
        'Coût estimé',
        'Note globale',
        'Note surface',
        'Note structure',
    ],
};

// Indicator selected when a layer is switched on and has no remembered choice.
export const LAYER_DEFAULT_INDICATOR: Record<LayerKey, string> = {
    sessions: 'Note globale',
    reference: 'Catégorie',
    network: 'État global',
    projects: 'Priorité',
};

// Second selector shown when the sessions indicator is "Note par type de dégradation".
export const SESSIONS_DEGRADATION_INDICATOR = 'Note par type de dégradation';
export const DEGRADATION_TYPES = ['Fissuration', 'Arrachement', 'Réparation', 'Remontées', 'Déformation'] as const;
export type DegradationType = typeof DEGRADATION_TYPES[number];

export type ElementaryFamilyKey = 'steps' | 'degradations' | 'events' | 'signV' | 'signH';

export const ELEMENTARY_FAMILIES: ReadonlyArray<{
    key: ElementaryFamilyKey;
    label: string;
    children: readonly string[];
}> = [
    { key: 'steps', label: 'Pas élémentaires', children: [] },
    {
        key: 'degradations',
        label: 'Dégradations',
        children: [
            'Fissures longitudinales',
            'Fissures transversales',
            'Faïençage',
            'Nids-de-poule',
            'Arrachements',
            'Ressuage',
            'Réparations',
        ],
    },
    { key: 'events', label: 'Évènements', children: ['Obstacle', "Dégât d'urgence", 'Signalement usager', 'Autre'] },
    { key: 'signV', label: 'Signalisation verticale', children: ['Danger', 'Police', 'Direction', 'Autre'] },
    {
        key: 'signH',
        label: 'Signalisation horizontale',
        children: ['Axiale', 'Rive', 'Passage piéton', 'Flèche', 'Autre'],
    },
];

export const BASEMAPS = ['plan', 'satellite', 'relief'] as const;
export type Basemap = typeof BASEMAPS[number];
